import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import styles from '../styles/pages/Shop.module.css';
import { ActionButton } from '@ax2/common/components/ActionButton';
import {
  getOrder,
  Order,
  ProductDescription,
  products,
  saveOrder,
} from '../rest/shopClient';
import DefaultPage from './DefaultPage';
import {
  getDropdownOptions,
  getRecommendations,
  getSummaryByPanel,
  PanelQuestion,
  PanelRecommendationAnswers,
  usePanelRecommendationContext,
} from '../contexts/PanelRecommendationContext';
import InfoBox from '../components/InfoBox';
import Box from '../components/Box';
import Dropdown, { DropdownOption } from '@ax2/common/components/Dropdown';
import PanelType from '@ax2/common/model/PanelType';
import checkIcon from '../assets/check_blue.svg';
import useMediaQuery from '@ax2/common/helpers/hooks/useMediaQuery';
import SignUpModal from '../components/SignUpModal';
import { formatUSD } from '../helpers/format';
import { useAuth } from '../helpers/hooks/useAuth';
import { useSticky } from '../helpers/hooks/useSticky';
import PanelSummaryModal from 'components/PanelSummaryModal';
import { physicianOptions } from '../helpers/physicianOptions';
const getFirstOptions = () => {
  return Object.entries(physicianOptions.QHCPSpecialty).map(
    ([title, value]) => ({
      value: Number(value),
      title,
    })
  );
};

const firstOptions = getFirstOptions();

function getAnswerText(
  question: PanelQuestion,
  answers: PanelRecommendationAnswers | null
): string {
  if (answers) {
    const option = getDropdownOptions(question).find(
      opt => opt.value === answers[question]
    );
    if (option) {
      return (option.title || '').replace(/[.]$/g, '');
    }
  }
  return '';
}

function countItems(order: Order): number {
  return _.sum(order.map(i => i.quantity));
}

const qtyOptions: DropdownOption<number>[] = _.range(1, 6).map(n => ({
  title: n.toString(),
  value: n,
}));

interface ShopItemProps {
  itemType: PanelType;
  product: ProductDescription;
  summary?: JSX.Element;
  rationale?: JSX.Element;
  onAdd: (itemType: PanelType, qty: number, specialty?: number) => boolean;
}

const ShopItem: React.FC<ShopItemProps> = ({
  itemType,
  summary,
  product,
  rationale,
  onAdd,
}) => {
  const mobileSmall = useMediaQuery('(max-width: 370px)');
  const [qty, setQty] = useState(1);
  const [physicanSpecialty, setPhysicanSpecialty] = useState(0);
  const delay = 3000;
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    let ignore = false;
    if (disabled) {
      setTimeout(() => {
        if (!ignore) {
          setDisabled(false);
        }
      }, delay);
    }
    return () => {
      ignore = true;
    };
  }, [disabled]);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  return (
    <Box key={itemType}>
      <div className={styles.item}>
        {isSummaryOpen && (
          <PanelSummaryModal
            panelType={itemType}
            summary={summary}
            onClose={() => setIsSummaryOpen(false)}
          />
        )}
        <div className={styles.itemTop}>
          <h4 className={styles.itemTitle}>{product.name}</h4>
          {rationale ? (
            <div className={styles.itemRationale}>
              <div className={styles.itemRationaleHeader}>
                Panel recommendation rationale
              </div>
              <div className={styles.itemRationaleText}>{rationale}</div>
            </div>
          ) : null}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {product.type === PanelType.PhysicianSurvey ? (
            <div
              style={{
                width: '80%',
                marginBottom: '12px',
              }}
            >
              <Dropdown
                options={firstOptions}
                value={physicanSpecialty}
                onChange={value => setPhysicanSpecialty(value)}
              />
            </div>
          ) : null}
          {/* product.type === PanelType.PhysicianSurvey &&
          physicanSpecialty &&
          getSecondOptions(physicanSpecialty).length !== 0 ? (
            <div style={{ width: '80%' }}>
              <Dropdown
                value={physician2}
                options={getSecondOptions(physicanSpecialty)}
                onChange={value => setPhysician2(value)}
              />
            </div>
          ) : null */}
        </div>
        <div className={styles.itemMiddle}>
          <ActionButton
            onClick={() => setIsSummaryOpen(true)}
            type="primary"
            text="Panel Summary & Survey Preview"
          />
        </div>
        <div className={styles.itemBottom}>
          <div className={styles.itemPrice}>
            <div className={styles.itemPriceValue}>
              ${formatUSD(product.price, 0)}
            </div>
            <div className={styles.itemPriceText}>Per Panel Survey</div>
          </div>
          <div className={styles.itemControls}>
            <div className={styles.itemQty}>
              <Dropdown
                options={qtyOptions}
                value={qty}
                onChange={value => setQty(value)}
              />
            </div>
            <div className={styles.itemButton}>
              <ActionButton
                onClick={() => {
                  if (onAdd(itemType, qty, physicanSpecialty)) {
                    setDisabled(true);
                  }
                }}
                text={
                  disabled
                    ? 'Added ' + qty + ' item' + (qty === 1 ? '' : 's')
                    : mobileSmall
                    ? 'Add to Cart'
                    : 'Add Panel Survey to Cart'
                }
                icon={
                  disabled
                    ? {
                        source: checkIcon,
                      }
                    : undefined
                }
                type="secondary"
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

const Shop: React.FC<RouteComponentProps> = ({ history }) => {
  const [activeTab, setActiveTab] = useState<'recommended' | 'other'>(
    'recommended'
  );
  const [order, setOrder] = useState<Order>(getOrder() || []);
  const updateAuth = useAuth(false);
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);
  const answers = usePanelRecommendationContext();
  const recommendations = answers ? getRecommendations(answers) : [];
  const title =
    recommendations.length > 0
      ? 'Your Recommended Panels (' + recommendations.length + ')'
      : 'Shop Panel Surveys';
  const subtitle =
    recommendations.length > 0
      ? 'Here you can view your recommendations and proceed to purchase their corresponding panel surveys.'
      : 'Here you can purchase panel surveys.';
  const totalItems = countItems(order);
  const answer1 = getAnswerText('q1', answers);
  const answer2 = getAnswerText('q2', answers);

  // Sticky shopping cart panel
  const cartPanelRef = useRef(null);
  const isSticky = useSticky(cartPanelRef);

  const addToCart = useCallback(
    (itemType: PanelType, qty: number, specialty?: number) => {
      if (itemType === 'PhysicianSurvey' && !specialty) {
        return false;
      }
      const oldOrderItem = order.filter(
        i => i.panelType === itemType && i.specialty === specialty
      );
      const restItems = order.filter(
        i => i.panelType !== itemType || i.specialty !== specialty
      );
      const s = specialty === 0 ? undefined : specialty;
      const newOrderItem = {
        panelType: itemType,
        specialty: s,
        quantity: _.isEmpty(oldOrderItem)
          ? qty
          : oldOrderItem[0].quantity + qty,
      };

      const newOrder: Order = [newOrderItem, ...restItems];
      setOrder(newOrder);
      saveOrder(newOrder);

      return true;
    },
    [order]
  );

  const purchase = useCallback(() => {
    if (countItems(order) > 0) {
      saveOrder(order);
      setIsVerifyingAuth(true);
      updateAuth()
        .then(result => {
          setIsVerifyingAuth(false);
          if (result) {
            history.push('/purchase');
          } else {
            setSignUpOpen(true);
          }
        })
        .catch(() => setIsVerifyingAuth(false));
    }
  }, [history, order, updateAuth]);

  let items: [string, ProductDescription][] = [];
  if (recommendations.length > 0) {
    const recommendedTypes = recommendations.map(r => r.type);
    items =
      activeTab === 'recommended'
        ? recommendations.map(r => [r.type, products[r.type]])
        : _.toPairs(products).filter(
            ([key]) => !_.includes(recommendedTypes, key as PanelType)
          );
  } else {
    // If there are no recommendations, show everything
    items = _.toPairs(products);
  }

  return (
    <DefaultPage
      header={{
        title: title,
        subtitle: subtitle,
        type: 'secondary',
      }}
    >
      <div
        className={[
          styles.cartPanelWrapper,
          isSticky ? styles.cartPanelWrapperSticky : '',
        ].join(' ')}
        ref={cartPanelRef}
      >
        <div className={styles.cartPanel}>
          <div className={styles.cart}>
            <div
              className={[
                styles.cartTotal,
                totalItems === 0 ? styles.cartEmpty : '',
              ].join(' ')}
            >
              {totalItems === 0 ? <>&mdash;</> : totalItems}
            </div>
            <div className={styles.cartTitle}>Your cart</div>
          </div>
          <ActionButton
            onClick={() => !isVerifyingAuth && purchase()}
            type={totalItems === 0 ? 'primary' : 'secondary'}
            text={totalItems === 0 ? 'Your cart is empty' : 'View/edit cart'}
            disabled={totalItems === 0}
            loading={isVerifyingAuth}
          />
        </div>
      </div>
      {answers && recommendations.length > 0 ? (
        <InfoBox
          header="Based on your answers:"
          text={
            <p>
              The following <strong>({recommendations.length})</strong> panels
              are recommended to give you expert answers that can help you move
              forward confidently. Your answers included{' '}
              <strong>{answer1}</strong> and <strong>{answer2}</strong>.
            </p>
          }
        />
      ) : null}
      {answers && recommendations.length > 0 ? (
        <div className={styles.buttonGroup}>
          <ActionButton
            onClick={() => setActiveTab('recommended')}
            size="large"
            type={activeTab === 'recommended' ? 'primary' : 'secondaryAlt'}
            text={'Your recommended panels (' + recommendations.length + ')'}
          />
          <ActionButton
            onClick={() => setActiveTab('other')}
            size="large"
            type={activeTab === 'other' ? 'primary' : 'secondaryAlt'}
            text={
              'Other panels (' +
              (Object.keys(products).length - recommendations.length) +
              ')'
            }
          />
        </div>
      ) : null}
      <div className={styles.items}>
        {items.map(([key, product]) => {
          const recommendation = _.find(
            recommendations,
            item => item.type === (key as PanelType)
          );
          return (
            <ShopItem
              key={key}
              itemType={key as PanelType}
              product={product}
              rationale={recommendation && recommendation.rationale}
              summary={recommendation && getSummaryByPanel(recommendation.type)}
              onAdd={addToCart}
            />
          );
        })}
      </div>
      <SignUpModal
        open={signUpOpen}
        setOpen={setSignUpOpen}
        signUpTopText={
          'Before proceeding to purchase, you must create an account ' +
          'with us which will give you access to your personal ' +
          'survey dashboard after your purchase is complete.'
        }
        signUpButtonText="Create account & proceed to purchase"
        signInButtonText="Sign in & proceed to purchase"
        onSuccess={() => history.push('/purchase')}
      />
    </DefaultPage>
  );
};

export default withRouter(Shop);
